/** What a room's page reads and keeps fresh, for the computer's page and Season's: the room, its last live, the series' groups. */
import { useCallback, useEffect, useState } from "react";
import type { HostRoomRow } from "../../../shared/talk";
import type { LiveChangeResponse, RoomAudienceResponse, WebinarLive } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { lastRun, replayBuilding } from "./liveWords";
import { useRoom } from "./useRoom";

/** How often the page asks after a live's replay, or a recording's render, while it is being made. */
export const POLL_MS = 8000;

export function useRoomPage(id: string) {
  const toast = useToast();
  const { detail, phase, refresh, putLive } = useRoom(id);
  const [lastLive, setLastLive] = useState<WebinarLive | null>(null);
  const [across, setAcross] = useState<RoomAudienceResponse | null>(null);
  const [acrossAgain, setAcrossAgain] = useState(0);

  // A series' last session is read on its own, with its report; a one-off live carries its own.
  const series = detail?.live?.series ?? null;
  const last = series ? lastRun(series.sessions) : null;
  const lastId = last?.id ?? null;
  useEffect(() => {
    if (!lastId) {
      setLastLive(null);
      return;
    }
    let alive = true;
    api.board
      .session(id, lastId)
      .then((s) => alive && setLastLive(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id, lastId]);

  // Across the series' groups, for its Sessions tab.
  const hasSeries = !!series;
  useEffect(() => {
    if (!hasSeries) return;
    let alive = true;
    api.webinars
      .audience(id, "series")
      .then((r) => alive && setAcross(r))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id, hasSeries, acrossAgain]);

  // While a live's replay is being made, the page asks again until it is not.
  const building = detail?.live ? replayBuilding(lastLive ?? detail.live) : false;
  useEffect(() => {
    if (!building) return;
    const iv = window.setInterval(() => {
      void refresh().catch(() => undefined);
      if (lastId) api.board.session(id, lastId).then(setLastLive, () => undefined);
    }, POLL_MS);
    return () => window.clearInterval(iv);
  }, [building, refresh, id, lastId]);

  const onSaved = useCallback(
    (res: LiveChangeResponse, said: string) => {
      putLive(res.live);
      toast(said);
    },
    [putLive, toast],
  );
  const reload = useCallback(() => void refresh().catch(() => undefined), [refresh]);
  const askAcrossAgain = useCallback(() => setAcrossAgain((x) => x + 1), []);

  return { detail, phase, series, last, lastLive, across, askAcrossAgain, onSaved, reload };
}

/** The room's conversations row, for the way in on its page; null for a room that is not published. */
export function useRoomTalk(id: string): HostRoomRow | null {
  const [talk, setTalk] = useState<HostRoomRow | null>(null);
  useEffect(() => {
    let alive = true;
    api.talk
      .room(id)
      .then((r) => alive && setTalk(r))
      .catch(() => alive && setTalk(null));
    return () => {
      alive = false;
    };
  }, [id]);
  return talk;
}
